/**
 * Pack 8.A — i18n framework.
 *
 * Wraps `i18next` + `react-i18next` while preserving the lightweight
 * `t / tn / useT / setLocale / getLocale / registerLocale` API that earlier
 * Super Admin pages already depend on. Variable interpolation continues to
 * use single-brace `{name}` placeholders so the existing translation
 * dictionaries (and the in-progress Super Admin sweep) remain valid.
 *
 * Changes vs. the prior hand-rolled implementation:
 *
 *  - Translation lookups now go through i18next, which gives us namespacing,
 *    nested keys, missing-key reporting, and React re-render support via
 *    `react-i18next` for components that subscribe through `useTranslation`.
 *  - `setLocale(...)` synchronously flips i18next's active language so the
 *    rest of the React tree re-renders.
 *  - Formatting helpers (`formatDate`, `formatNumber`, `formatCurrency`, …)
 *    live in `./formatters` and honour locale-specific numbering systems
 *    (Arabic-Indic numerals for `ar-*` per draft #713).
 *  - RTL detection lives in `./formatters` (`isRtlLocale`) and is consumed
 *    by `LocaleProvider` to set `document.documentElement.dir`.
 *
 * Components built before this refactor used `t(key, fallback?, vars?)`. That
 * signature is preserved verbatim — it now resolves through i18next under
 * the hood.
 */

import i18next, { type i18n as I18nInstance, type InitOptions } from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";

// Task #2050 (UX Audit Phase 2 — T12) — Lazy locale loading.
// Only English is eagerly loaded (it's always needed for fallback).
// All other locale dictionaries are fetched on-demand when setLocale()
// switches to them for the first time. This keeps the initial JS bundle
// lean (~35 KB saved) without changing the synchronous `t()` API: en
// strings are always available immediately; a tiny flash of English may
// appear during the first load of an alternate locale on cold start.
import en from "./locales/en.json";

export type LocaleDict = Record<string, string>;

const DEFAULT_LOCALE = "en";
const DEFAULT_NS = "translation";

/** Tracks which non-English locales have been fetched already. */
const loadedLocales = new Set<string>(["en"]);

/** Lazy-load map: resource key → dynamic import. Vite splits each into its own chunk. */
const LAZY_LOCALES: Record<string, () => Promise<{ default: LocaleDict }>> = {
  ar: () => import("./locales/ar.json"),
  fa: () => import("./locales/fa.json"),
  ur: () => import("./locales/ur.json"),
  hi: () => import("./locales/hi.json"),
};

let initialised = false;

function ensureInit(): I18nInstance {
  if (initialised) return i18next;
  initialised = true;
  // The `react` block is contributed by `react-i18next`'s module
  // augmentation; in projects that don't pull the augmentation reliably
  // (we only import `useTranslation` so types may resolve to the bare
  // i18next InitOptions), we cast through a typed alias to keep both
  // i18next's and react-i18next's option fields acceptable.
  const opts: InitOptions & { react?: { useSuspense?: boolean } } = {
    lng: DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: DEFAULT_NS,
    ns: [DEFAULT_NS],
    resources: {
      en: { [DEFAULT_NS]: en as LocaleDict },
    },
    interpolation: {
      // Keep single-brace placeholders so legacy dictionaries continue
      // to interpolate correctly (`Edit: {name}` ➜ `Edit: Acme`).
      prefix: "{",
      suffix: "}",
      escapeValue: false,
    },
    returnNull: false,
    react: { useSuspense: false },
  };
  void i18next.use(initReactI18next).init(opts);
  return i18next;
}

/** Ensure a locale dictionary is registered in i18next before switching to it. */
async function ensureLocaleLoaded(resourceKey: string): Promise<void> {
  if (loadedLocales.has(resourceKey)) return;
  const loader = LAZY_LOCALES[resourceKey];
  if (!loader) return; // unknown locale — fall through to en fallback
  try {
    const mod = await loader();
    i18next.addResourceBundle(resourceKey, DEFAULT_NS, mod.default as LocaleDict, true, true);
    loadedLocales.add(resourceKey);
  } catch {
    // Silently fall back to English if the chunk fails to load
  }
}

ensureInit();

/**
 * Switch the active locale. A bare BCP 47 tag is normalised to the
 * underlying language (e.g. `ar-SA` ➜ keys resolve from the `ar`
 * dictionary). The full BCP 47 tag is preserved for `Intl.*` formatting.
 */
export function setLocale(locale: string | null | undefined): void {
  ensureInit();
  const next = (locale && locale.length > 0 ? locale : DEFAULT_LOCALE);
  // Track the full BCP 47 tag so formatters can consume it directly.
  fullLocale = next;
  // i18next looks up resources by language key; map `ar-SA` ➜ `ar` etc.
  const resourceKey = next.split(/[-_]/)[0].toLowerCase();
  // Task #2050 (T12) — lazy-load the locale dictionary for non-English
  // locales, then switch i18next language. During the async fetch the app
  // continues to render in English (fallback), then i18next re-renders once
  // the bundle arrives. This path is synchronous for English (already loaded).
  if (resourceKey === DEFAULT_LOCALE || loadedLocales.has(resourceKey)) {
    if (i18next.language !== resourceKey) {
      void i18next.changeLanguage(resourceKey);
    }
  } else {
    void ensureLocaleLoaded(resourceKey).then(() => {
      if (i18next.language !== resourceKey) {
        void i18next.changeLanguage(resourceKey);
      }
    });
  }
}

let fullLocale: string = DEFAULT_LOCALE;

export function getLocale(): string {
  return fullLocale;
}

/**
 * Register / merge a runtime translation dictionary for an additional
 * locale. Useful for lazy-loading translation packs from the server or
 * when test code needs to inject a single string without rebuilding the
 * static resource map.
 */
export function registerLocale(locale: string, dict: LocaleDict): void {
  ensureInit();
  const resourceKey = locale.split(/[-_]/)[0].toLowerCase();
  i18next.addResourceBundle(resourceKey, DEFAULT_NS, dict, true, true);
}

/**
 * Translate a key using the active locale.
 *
 * @param key       Dot-separated key, e.g. `"sa.tenants.title"`.
 * @param fallback  String to return if the key is missing. Defaults to the key.
 * @param vars      Optional interpolation variables (single-brace `{name}`).
 */
export function t(
  key: string,
  fallback?: string,
  vars?: Record<string, string | number>,
): string {
  ensureInit();
  const value = i18next.t(key, {
    defaultValue: fallback ?? key,
    ...(vars ?? {}),
  });
  return typeof value === "string" ? value : String(value);
}

/**
 * Resolve the CLDR plural form for a given count and locale.
 *
 * Supports Arabic 6-form (zero, one, two, few, many, other) per CLDR.
 * For all other locales falls back to the classic two-form (one / other).
 *
 * Arabic CLDR rules (Unicode CLDR 44):
 *   zero  — n = 0
 *   one   — n = 1
 *   two   — n = 2
 *   few   — 3 ≤ n%100 ≤ 10
 *   many  — 11 ≤ n%100 ≤ 99
 *   other — everything else (n = 0 maps to zero first)
 */
function resolvePluralForm(count: number, locale: string): string {
  const lang = locale.split(/[-_]/)[0].toLowerCase();
  if (lang === "ar") {
    if (count === 0) return "zero";
    if (count === 1) return "one";
    if (count === 2) return "two";
    const mod = Math.abs(count) % 100;
    if (mod >= 3 && mod <= 10) return "few";
    if (mod >= 11 && mod <= 99) return "many";
    return "other";
  }
  // Two-form for all other locales
  return count === 1 ? "one" : "other";
}

/**
 * Plural-aware translate (Task #2051 — Arabic CLDR 6-form).
 *
 * Looks up `${key}.<form>` where `<form>` is resolved by CLDR plural rules
 * for the active locale.  For Arabic this produces up to 6 forms
 * (zero, one, two, few, many, other).  Falls back to `${key}.other` if the
 * specific form key is missing so existing two-form dictionaries continue to
 * work correctly in Arabic for uncategorised counts.
 *
 * Dictionary convention (unchanged for non-Arabic):
 *   "items.one":   "1 item"
 *   "items.other": "{count} items"
 *
 * Extended Arabic convention:
 *   "items.zero":  "لا توجد عناصر"
 *   "items.one":   "عنصر واحد"
 *   "items.two":   "عنصران"
 *   "items.few":   "{count} عناصر"
 *   "items.many":  "{count} عنصرًا"
 *   "items.other": "{count} عنصر"
 */
export function tn(
  key: string,
  count: number,
  fallback?: string,
  vars?: Record<string, string | number>,
): string {
  const form = resolvePluralForm(count, fullLocale);
  const specificKey = `${key}.${form}`;
  // Try specific form first; if missing fall back to .other
  const specific = t(specificKey, /* no fallback = returns key */ undefined, { count, ...(vars ?? {}) });
  if (specific !== specificKey) return specific;
  // Fall back to .other
  return t(`${key}.other`, fallback, { count, ...(vars ?? {}) });
}

/**
 * React hook variant — returns the `t / tn` translators plus the active
 * locale tag. Wrapping `useTranslation` ensures consumer components
 * re-render when the active language changes via `setLocale`.
 */
export function useT() {
  // Subscribing keeps re-renders honest after `setLocale` calls.
  useTranslation();
  return { t, tn, locale: fullLocale };
}

/** Direct access to the underlying i18next instance — escape hatch only. */
export function getI18n(): I18nInstance {
  return ensureInit();
}

export {
  formatDate,
  formatTime,
  formatDateTime,
  formatNumber,
  formatCurrency,
  formatPercent,
  formatRelativeTime,
  isRtlLocale,
  toArabicIndicDigits,
  withNumberingSystem,
} from "./formatters";
